import type { ReactElement } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Icon, type IconName } from '@/components/atoms/icon';
import { cn } from '@/lib/cn';

type Tab = {
  readonly to: '/' | '/library' | '/practice' | '/repertoire' | '/history';
  readonly icon: IconName;
  readonly label: string;
  readonly isPractice?: boolean;
  readonly matchPrefix?: readonly string[];
};

const TABS: readonly Tab[] = [
  { to: '/', icon: 'home', label: 'Today' },
  { to: '/library', icon: 'book_open', label: 'Library', matchPrefix: ['/library'] },
  { to: '/practice', icon: 'play', label: 'Practice', isPractice: true, matchPrefix: ['/practice'] },
  { to: '/repertoire', icon: 'book', label: 'Repertoire', matchPrefix: ['/repertoire'] },
  { to: '/history', icon: 'chart', label: 'History', matchPrefix: ['/history', '/insights'] },
] as const;

function isTabActive(pathname: string, tab: Tab): boolean {
  if (tab.to === '/') return pathname === '/';
  const prefixes = tab.matchPrefix ?? [tab.to];
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function BottomNav(): ReactElement {
  const location = useLocation();

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-[var(--rule)] bg-[var(--bg-surface)] lg:hidden"
    >
      <div className="mx-auto grid max-w-lg grid-cols-5 items-center h-16 relative">
        {TABS.map((tab) => {
          const isActive = isTabActive(location.pathname, tab);
          if (tab.isPractice) {
            return (
              <Link
                key={tab.to}
                to={tab.to}
                data-nav-practice="true"
                className={cn(
                  'flex flex-col items-center justify-end pb-2 relative',
                  isActive ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]',
                )}
              >
                <span
                  className={cn(
                    'absolute -top-4 w-12 h-12 rounded-full flex items-center justify-center',
                    isActive
                      ? 'bg-[var(--ink)] text-white'
                      : 'bg-[var(--brand)] text-white shadow-[var(--shadow-1)]',
                  )}
                  aria-hidden
                >
                  <Icon name="play" size={18} />
                </span>
                <span className="text-[10px] font-semibold mt-6">{tab.label}</span>
              </Link>
            );
          }
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                'flex flex-col items-center justify-center gap-1 transition-colors',
                isActive ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]',
              )}
            >
              <Icon name={tab.icon} size={18} />
              <span className="text-[10px] font-semibold">{tab.label}</span>
            </Link>
          );
        })}
      </div>
      <div className="h-[env(safe-area-inset-bottom)]" aria-hidden />
    </nav>
  );
}
